import {Injectable, OnDestroy} from '@angular/core';
import {IPublishOptions, MqttService} from 'ngx-mqtt';
import {BehaviorSubject, Subject, Subscription} from 'rxjs';
import {map, takeUntil} from 'rxjs/operators';

/** A split leg as the customer display describes it (native MqttManager.SplitInfo). */
export interface SplitDisplayInfo {
  index: number;      // 1-based position of the leg
  count: number;
  remaining: number;  // balance still due AFTER this leg
  label: string;
  complete?: boolean;
}

/** Tender detail for the customer display (native MqttManager.PaymentDoneDetail). */
export interface PaymentDisplayDetail {
  tender: string;     // cash | card | gift | giftcard | keyed | other | manual (lower case)
  total: number;      // amount due for this tender
  tendered: number;   // handed over (cash); = total otherwise
  change: number;     // never negative
}

/** Store flag native_payment_prompts (ENG-5964): the display's in-app payment prompts and the
 *  extra messages that drive them. Off = the display gets exactly what it got before. */
export function nativePromptsOn(store: any): boolean {
  return store?.feature_flag?.['native_payment_prompts'] === true;
}

const round2 = (n: any) => Math.round((Number(n) || 0) * 100) / 100;

@Injectable()
export class FrontFacingMqttService implements OnDestroy{
  paymentModeSubject = new BehaviorSubject<any>({});
  paymentMode = this.paymentModeSubject.asObservable();
  private tipDataSubject = new BehaviorSubject<any>({});
  tipData = this.tipDataSubject.asObservable();
  paymentModeSubscription: Subscription;
  private tipDataSubscription: Subscription;
  private destroySubject: Subject<void> = new Subject<void>();

  private decoder;
  constructor(private mq: MqttService) {  }

  ngOnDestroy() {
    this.destroySubject.next();
  }

  setCurrentOrder(data) {
    const qos = 2;
    const topicName = 'currentOrder/' + localStorage.getItem('selectedTerminal');
    this.mq.unsafePublish(topicName, JSON.stringify(data), { qos } as IPublishOptions);
  }

  getPaymentMode() {
    if (this.paymentModeSubscription) {
      this.paymentModeSubscription.unsubscribe();
    }
    const topicName = 'paymentMode/' + localStorage.getItem('selectedTerminal');
    this.paymentModeSubscription = this.mq.observe(topicName).pipe(takeUntil(this.destroySubject)).subscribe(data => {
      const parsedData = this.decodeAndParse(data.payload);
      this.paymentModeSubject.next(parsedData);
    });
  }

  resetPaymentMode(){
    if (this.paymentMode){
      this.paymentModeSubject.next({});
    }
  }

  destroyPaymentModeSubscription(){
    if (this.paymentModeSubscription) {
      this.paymentModeSubscription.unsubscribe();
    }
  }

  setPaymentMode(paymentData){
    const qos = 2;
    const paymentModeTopicName = 'paymentModeSub/'+localStorage.getItem('selectedTerminal')
    this.mq.unsafePublish(paymentModeTopicName,JSON.stringify(paymentData),{qos} as IPublishOptions)
   }

  decodeAndParse(payload) {
    this.decoder = new TextDecoder('utf-8');
    return JSON.parse(this.decoder.decode(payload));
  }

  getTipData() {
    if (this.tipDataSubscription) {
      this.tipDataSubscription.unsubscribe();
    }
    const topicName = 'tippub/' + localStorage.getItem('selectedTerminal');
    this.tipDataSubscription = this.mq.observe(topicName).pipe(takeUntil(this.destroySubject)).subscribe(data => {
      this.tipDataSubject.next(this.decodeAndParse(data.payload));
    });
  }

  publishTip(tipData) {
    const qos = 2;
    const tipTopicName = 'tippub/' + localStorage.getItem('selectedTerminal');
      this.mq.unsafePublish(tipTopicName, JSON.stringify(tipData), { qos } as IPublishOptions);
  }

  resetTip (){
    this.tipDataSubject.next({})
  }

  goBackMqttPub() {
    const qos = 2;
    const payload = { goBack: true };
    const topicName = `goback/${localStorage.getItem('selectedTerminal')}/pub`;
      this.mq.unsafePublish(topicName, JSON.stringify(payload), { qos } as IPublishOptions);
  }

  goBackMqttSub() {
    const topicName = `goback/${localStorage.getItem('selectedTerminal')}/sub`;
      return this.mq.observe(topicName);
  }

  getPaymentStart() {
    
    const topicName = 'paymentStartFront/' + localStorage.getItem('selectedTerminal');
      return this.mq.observe(topicName);
  }

  publishStartPayment() {
    const payload = { onpay: true };
    const qos = 2;
    const topicName = 'paymentStart/' + localStorage.getItem('selectedTerminal');
      this.mq.unsafePublish(topicName, JSON.stringify(payload), { qos } as IPublishOptions);
  }

  getPaymentCancel() {
    const topicName = 'paymentCancel/' + localStorage.getItem('selectedTerminal');
      return this.mq.observe(topicName);
  }

  publishPaymentCancel() {
    const payload = { oncancel: true };
    const qos = 2;
    const topicName = 'paymentCancel/' + localStorage.getItem('selectedTerminal');
      this.mq.unsafePublish(topicName, JSON.stringify(payload), { qos } as IPublishOptions);
  }

  publishPaymentDone() {
    const payload = { type: 'paymentCompletedDone' };
    const qos = 2;
    const topicName = 'paymentDone/' + localStorage.getItem('selectedTerminal');
      this.mq.unsafePublish(topicName, JSON.stringify(payload), { qos } as IPublishOptions);
  }

  /**
   * paymentDone for a paid order. With the store's native_payment_prompts flag it also names
   * the paid order (so the display's receipt prompt can't pick up the NEXT order) and, when
   * given, the tender detail ("Paid in cash" with the change) or just the tender (a cashier-
   * recorded payment: keyed, giftcard, manual, other → "Payment recorded"). Flag off =
   * publishPaymentDone().
   */
  publishPaymentDoneFor(store: any, orderHash?: string, detail?: PaymentDisplayDetail, tender?: string) {
    if (!nativePromptsOn(store)) { this.publishPaymentDone(); return; }
    const payload: any = { type: 'paymentCompletedDone' };
    if (orderHash) { payload.orderHash = orderHash; }
    if (detail) {
      payload.tender = detail.tender;
      payload.total = round2(detail.total);
      payload.tendered = round2(detail.tendered);
      payload.change = round2(Math.max(0, Number(detail.change) || 0));
    } else if (tender) {
      payload.tender = tender;
    }
    const topicName = 'paymentDone/' + localStorage.getItem('selectedTerminal');
    this.mq.unsafePublish(topicName, JSON.stringify(payload), { qos: 2 } as IPublishOptions);
  }

  /**
   * Payment taken, the moment it is (cash / keypad close succeeded, terminal approved) and
   * before the cashier's Done / print choice that sends paymentDone. Flag off: nothing.
   */
  publishPaymentApprovedFor(store: any, detail: PaymentDisplayDetail, split?: SplitDisplayInfo) {
    if (!nativePromptsOn(store)) { return; }
    this.publishPaymentApproved({
      tender: detail.tender,
      total: round2(detail.total),
      tendered: round2(detail.tendered),
      change: round2(Math.max(0, Number(detail.change) || 0)),
    }, split);
  }

  /** Split progress for the display's order overview; {active:false} clears it. Flag off: nothing. */
  publishSplitStatusFor(store: any, status: any) {
    if (!nativePromptsOn(store)) { return; }
    const topicName = 'splitStatus/' + localStorage.getItem('selectedTerminal');
    this.mq.unsafePublish(topicName, JSON.stringify(status), { qos: 2 } as IPublishOptions);
  }

  /** reason (QA 2026-10-18, same values the native POS sends): 'declined' | 'cancelled' |
   *  'timeout' | 'busy' | 'unconfirmed' | 'cancelled_pos'. Omitted = a plain decline. */
  publishPaymentFailed(reason?: string) {
    const payload: any = { type: 'paymentFailed' };
    if (reason) { payload.reason = reason; }
    const qos = 2;
    const topicName = 'paymentDone/' + localStorage.getItem('selectedTerminal');
    this.mq.unsafePublish(topicName, JSON.stringify(payload), { qos } as IPublishOptions);
  }

  /** A split leg's prompt on the customer display (native MqttManager.publishPaymentModeSub):
   *  the tender, the leg amount and where the leg sits in the split. */
  publishPaymentModeSub(mode: string, amount?: number, split?: SplitDisplayInfo) {
    const payload: any = { paymentMode: mode };
    if (amount !== undefined && amount !== null) { payload.amount = Math.round(amount * 100) / 100; }
    if (split) { payload.split = { ...split, remaining: Math.round(split.remaining * 100) / 100 }; }
    const topicName = 'paymentModeSub/' + localStorage.getItem('selectedTerminal');
    this.mq.unsafePublish(topicName, JSON.stringify(payload), { qos: 2 } as IPublishOptions);
  }

  /** One split leg approved (native publishPaymentApproved): the display shows "Approved"
   *  for the leg instead of the whole order's "payment done" screen. */
  publishPaymentApproved(detail: { tender: string; total: number; tendered: number; change: number },
                         split?: SplitDisplayInfo) {
    const payload: any = { approved: true, ...detail };
    if (split) { payload.split = { ...split, remaining: Math.round(split.remaining * 100) / 100 }; }
    const topicName = 'paymentApproved/' + localStorage.getItem('selectedTerminal');
    this.mq.unsafePublish(topicName, JSON.stringify(payload), { qos: 2 } as IPublishOptions);
  }

  // type 'card' is published by the card dialog's Retry Payment so the
  // front-facing screen leaves its decline state and shows "present card" again.
  publishPaymentInProgress(type: 'cash' | 'card' = 'cash') {
    const payload = { inprogress: true, type };
    const qos = 2;
    const topicName = 'paymentInProgress/' + localStorage.getItem('selectedTerminal');
    this.mq.unsafePublish(topicName, JSON.stringify(payload), { qos } as IPublishOptions);
  }

  getRetryPayment() {
    const topicName = 'retryPayment/' + localStorage.getItem('selectedTerminal');
      return this.mq.observe(topicName);
  }

  publishSignatureStart(msg) {
    const payload = { startSignature: true, msg: msg };
    const qos = 2;
    const topicName = 'signatureStart/' + localStorage.getItem('selectedTerminal');
      this.mq.unsafePublish(topicName, JSON.stringify(payload), { qos } as IPublishOptions);
  }

  getPaymentDoneStatus() {
    const topicName = 'paymentDone/' + localStorage.getItem('selectedTerminal');
      return this.mq.observe(topicName);
  }

  getSignatureDoneStatus() {
    const topicName = 'signatureDone/' + localStorage.getItem('selectedTerminal');
      return this.mq.observe(topicName);
  }

  refreshOpenOrder (){
    const qos = 2
    const payload = { refreshOpenOrder: true,  };
    const topicName = 'refreshOpenOrder/' + localStorage.getItem('selectedTerminal');
    this.mq.unsafePublish(topicName, JSON.stringify(payload), { qos } as IPublishOptions);
  }

  publishToFrontFacing(tip){
    const qos = 2;
    const tipTopicName = 'fromff/' + localStorage.getItem('selectedTerminal');
    this.mq.unsafePublish(tipTopicName,JSON.stringify(tip),{qos} as IPublishOptions)
  }

  listenFromFrontFacing(){
    const topicName = 'toff/' + localStorage.getItem('selectedTerminal');
    return this.mq.observe(topicName).pipe(map((x) => this.decodeAndParse(x.payload)))
  }

  listenForDeviceConnections(){
    const topic = `device/${localStorage.getItem('selectedTerminal')}`
    return this.mq.observe(topic).pipe(map((x) => this.decodeAndParse(x.payload)))
  }

  listenForDeviceDisconnection(){
    const topic = `device/disconnect/${localStorage.getItem('selectedTerminal')}`
    return this.mq.observe(topic).pipe(map((x) => this.decodeAndParse(x.payload)))
  }
}
